/**
 * The playhead: ONE vertical line at the bar the engine is on. Every bar stays normally visible.
 *
 * <h2>The veil was REMOVED 2026-10-02, deliberately, and here is the trade</h2>
 *
 * This used to draw a translucent `markArea` over everything after the playhead, on the argument
 * that a chart showing the whole history while claiming to replay a decision is showing the viewer
 * the answer. That argument is sound and the implementation was not worth it: a band whose left
 * edge moves every bar is a full-height repaint per tick, which flickers, and at 62% opacity it
 * made the price action on the right-hand two-thirds of the chart hard to read — including the
 * bars a viewer scrubs back and forth to compare against.
 *
 * <p><b>What is lost is a VISUAL guard, not a real one.</b> The engine's own look-ahead protection
 * is elsewhere and unaffected: `MarketContext` refuses to answer once the engine has moved past
 * the bar that owned it, and the replay feeds bars strictly in order. The veil only ever stopped a
 * HUMAN reading ahead; it never constrained the FSM. Anyone reading a replay still has to remember
 * that bars right of the line are not evidence for the decision at the line.
 *
 * <h2>markLine, on a series of its own</h2>
 *
 * Attached to a dedicated, empty, silent series rather than to the candlesticks. The candlestick
 * series already carries the pattern feature's marks, and a second writer there replaces the first
 * — a lesson this codebase has already paid for. An own series also means the whole decoration
 * disappears by dropping one overlay id.
 *
 * `markLine` rather than a `lines` series because it spans the grid's full height by itself. A
 * `lines` series needs concrete y coordinates, which would have to be recomputed from the visible
 * price range on every zoom.
 *
 * <h2>Pure</h2>
 *
 * A date and the chart's dates in, an overlay out.
 */

export const REPLAY_PLAYHEAD_OVERLAY_ID = 'dynaman-replay-playhead';

/**
 * The ECharts series id, which is what makes the playhead cheap to move.
 *
 * <p>The first version moved the playhead through the React overlay list, so every tick re-ran
 * `processChartData` over every candle and every other overlay. ECharts merges a series by `id`
 * without touching its siblings, so the per-tick update is now one `setOption` carrying only the
 * two marks — measured at ~74ms sustained, against a full rebuild of the whole option.
 *
 * <p>The overlay still exists in React state: it is what CREATES the series and what removes it
 * on close. It is simply not rewritten on every bar.
 *
 * <p><b>A correction.</b> An earlier version of this note claimed the rebuild cost ~1.2 seconds a
 * bar. That figure came from a timer-based measurement in a HIDDEN browser tab, where Chrome
 * clamps setTimeout to about once a second — it measured the clamp, not the chart. The 74ms
 * figure above is synchronous and unaffected.
 */
export const REPLAY_PLAYHEAD_SERIES_ID = 'dynaman-replay-playhead-series';

/**
 * The marks, as ECharts wants them.
 *
 * <p>ONE definition, used both by the renderer that creates the series and by the per-tick merge
 * that moves it. Two would drift, and the symptom would be a playhead that looks right when the
 * chart rebuilds and wrong while it is playing — which reads as a rendering glitch rather than
 * as duplicated code.
 */
export function playheadMarks(overlay) {
  if (!overlay) return null;
  return {
    markLine: {
      silent: true,
      symbol: 'none',
      animation: false,
      label: overlay.headLabel
        ? {
          show: true,
          formatter: overlay.headLabel,
          position: 'insideEndTop',
          color: overlay.color,
          fontSize: 11,
          fontWeight: 'bold',
        }
        : { show: false },
      // WIDTH 2, up from 1.5. The line is now the only indication of where the engine is -- it no
      // longer has a shaded region beside it doing half the work -- so it has to be findable at a
      // glance on a chart carrying candles, trade markers and a trendline.
      lineStyle: { color: overlay.color, width: 2, type: 'solid', opacity: 0.95 },
      data: [{ xAxis: overlay.date }],
    },
    // An EMPTY markArea, not an absent one. A merge cannot REMOVE a mark by omitting it, so a
    // chart that still has a veil from before this change -- a tab left open across a reload, or a
    // series created by an older bundle -- would keep it frozen over the right-hand edge for the
    // rest of the replay. Declaring it empty clears it once and costs nothing thereafter.
    markArea: { silent: true, animation: false, data: [] },
  };
}

/** The pen. Amber, because green and red already mean up and down on a candlestick chart. */
export const PLAYHEAD_COLOR = '#ffb300';

/**
 * Builds the overlay.
 *
 * @param currentDate the session date the playhead sits on, `yyyy-MM-dd`
 * @param dates       the chart's own x-axis categories
 * @returns an overlay, or null when the date is not on the chart — null rather than an overlay
 *          with no marks, so the caller removes the previous playhead instead of drawing an empty
 *          one that looks like a rendering failure.
 */
export function buildPlayheadOverlay(currentDate, dates, { label = null } = {}) {
  const axis = Array.isArray(dates) ? dates : [];
  if (!currentDate || axis.length === 0) return null;

  const at = axis.indexOf(currentDate);
  if (at < 0) return null;

  return {
    id: REPLAY_PLAYHEAD_OVERLAY_ID,
    type: 'replayplayhead',
    label: 'DynaMan replay',
    date: currentDate,
    color: PLAYHEAD_COLOR,
    headLabel: label,
    barIndex: at,
    lastIndex: axis.length - 1,
  };
}

/**
 * The window the chart should show so the playhead stays visible with context behind it.
 *
 * <h2>It pans, it does not centre</h2>
 *
 * Centring the playhead puts half the viewport into bars the engine has not reached. The playhead
 * is placed at {@code HEAD_POSITION} of the way across instead, so most of the visible chart is
 * the past the engine has actually seen, with enough ahead of it in frame to follow where price
 * goes next.
 *
 * <h2>BAR INDICES, not percentages, and that distinction is load-bearing</h2>
 *
 * `EChartComponent` configures its dataZoom with `startValue`/`endValue` and its `datazoom`
 * handler records ONLY those two fields into `zoomRef`. A `dispatchAction` carrying `start`/`end`
 * percentages therefore moves the view and is never recorded — so the next option rebuild, which
 * any overlay update triggers, restores the stale window and the chart snaps back.
 *
 * <p>That is not hypothetical: a comment in that handler documents the same failure breaking the
 * Elliott "focus on the count" feature, and the first version of this function reproduced it
 * exactly. Percentages also invited an arithmetic bug that produced an end of 211%.
 *
 * @returns `{startValue, endValue}` as bar indices, or null when there is nothing to pan
 */
export const HEAD_POSITION = 0.72;

export function viewWindowFor(barIndex, totalBars, visibleBars = 180) {
  if (!Number.isFinite(barIndex) || !Number.isFinite(totalBars) || totalBars <= 1) return null;

  // The 20-bar floor stops the view collapsing to a couple of candles, but it must never exceed
  // the data.
  const span = Math.min(totalBars, Math.max(20, Math.min(visibleBars, totalBars)));

  let first = Math.round(barIndex - span * HEAD_POSITION);
  first = Math.max(0, Math.min(first, totalBars - span));

  return { startValue: first, endValue: first + span - 1 };
}
